import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../hooks/useNotification';
import { useSocket } from '../contexts/SocketContext';
import missionService from '../services/missionService';
import contractorService from '../services/contractorService';
import LoadingSpinner from '../components/LoadingSpinner';
import { debugLog } from '../utils/debug';
import TabPanel from '../components/TabPanel';
import IncidentTab from '../components/mission/IncidentTab';
import LZSafetyTab from '../components/mission/LZSafetyTab';
import PatientTab from '../components/mission/PatientTab';
import ResourcesTab from '../components/mission/ResourcesTab';
import CommunicationsTab from '../components/mission/CommunicationsTab';
import CommunicationsPanel from '../components/mission/CommunicationsPanel';
import TimelineView from '../components/mission/TimelineView';
import StatusUpdateModal from '../components/mission/StatusUpdateModal';
import CloseMissionModal from '../components/mission/CloseMissionModal';
import ElapsedTimeTracker from '../components/mission/ElapsedTimeTracker';
import StatusStepper from '../components/mission/StatusStepper';
import SafetyTimerModal from '../components/mission/SafetyTimerModal';
import SendAlertModal from '../components/mission/SendAlertModal';
import PrintMissionReport from '../components/mission/PrintMissionReport';
import {
  ArrowLeftIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  BellAlertIcon,
  PrinterIcon,
  MapPinIcon,
  PhoneIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { Mission, AircraftContractor, Priority, MissionStatus } from '../types';
import { formatMissionType, formatMissionStatus, formatTimeAgo } from '../utils/formatUtils';
import DisconnectedBanner from '../components/DisconnectedBanner';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

import clsx from 'clsx';

const MissionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { socket, joinMission, leaveMission } = useSocket();
  
  const [mission, setMission] = useState<Mission | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('incident');
  const [contractors, setContractors] = useState<AircraftContractor[]>([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showSafetyTimer, setShowSafetyTimer] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [showCommsPanel, setShowCommsPanel] = useState(false);
  const [concurrentEditWarning, setConcurrentEditWarning] = useState<string | null>(null);

  // Ref to prevent duplicate alert sends
  const alertSendingRef = useRef(false);

  // Fetch mission data
  useEffect(() => {
    if (!id) return;
    
    const fetchMission = async () => {
      try {
        const [missionData, contractorsData] = await Promise.all([
          missionService.getMission(id),
          contractorService.getContractors()
        ]);
        setMission(missionData);
        setContractors(contractorsData);
        
        // Join mission room for real-time updates
        joinMission(id);
      } catch (error: any) {
        addNotification({
          type: 'error',
          title: 'Failed to load mission',
          message: error.message
        });
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchMission();

    // Leave mission room on unmount
    return () => {
      if (id) leaveMission(id);
    };
  }, [id, navigate, addNotification, joinMission, leaveMission]);

  // Socket listeners for real-time updates
  useEffect(() => {
    if (!socket || !id) return;

    // Join the mission room
    socket.emit('mission:join', id);
    debugLog(`[Socket.IO] Joined mission room: mission_${id}`);

    const handleMissionUpdate = (data: any) => {
      if (data.missionId === id) {
        if (data.updatedBy && data.updatedBy !== user?.fullName && data.updatedBy !== user?.username) {
          setConcurrentEditWarning(data.updatedBy);
        }
        setMission(prev => prev ? { ...prev, ...data.changes } : null);
        addNotification({
          type: 'info',
          title: 'Mission Updated',
          message: `Updated by ${data.updatedBy}`
        });
      }
    };

    const handleNewComment = (data: any) => {
      if (data.missionId === id) {
        setMission(prev => prev ? {
          ...prev,
          // Comments are newest-first (LIFO) — prepend, dropping any dupe
          // already added by the optimistic submit path
          comments: [data.comment, ...(prev.comments || []).filter(c => c.id !== data.comment.id)]
        } : null);
      }
    };

    const handlePatientAdded = (data: any) => {
      debugLog('WebSocket: patient:added event received', data);
      if (data.missionId === id) {
        // Refresh the entire mission to ensure consistency
        missionService.getMission(id).then(updated => {
          debugLog('WebSocket: Mission refreshed after patient added', {
            patientCount: updated.patientCount,
            patientsLength: updated.patients?.length
          });
          setMission(updated);
        });
      }
    };

    const handlePatientUpdated = (data: any) => {
      if (data.missionId === id) {
        missionService.getMission(id).then(setMission);
      }
    };

    socket.on('mission_updated', handleMissionUpdate);
    socket.on('comment_added', handleNewComment);
    socket.on('patient:added', handlePatientAdded);
    socket.on('patient:updated', handlePatientUpdated);

    return () => {
      // Leave the mission room on cleanup
      socket.emit('mission:leave', id);
      debugLog(`[Socket.IO] Left mission room: mission_${id}`);

      socket.off('mission_updated', handleMissionUpdate);
      socket.off('comment_added', handleNewComment);
      socket.off('patient:added', handlePatientAdded);
      socket.off('patient:updated', handlePatientUpdated);
    };
  }, [socket, id, addNotification]);

  const handleUpdateMission = async (updates: Partial<Mission>) => {
    if (!mission) return;
    
    try {
      const updated = await missionService.updateMission(mission.id, updates);
      setMission(updated);
      addNotification({
        type: 'success',
        title: 'Mission updated',
        message: 'Changes saved successfully'
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Update failed',
        message: error.message
      });
    }
  };

  const handleStatusChange = async (newStatus: MissionStatus, notes?: string) => {
    if (!mission) return;
    
    try {
      // Map status to appropriate timestamp field
      const statusToFieldMap: Record<string, string | null> = {
        'RECEIVED': 'timeReceived',
        'ASSIGNED': 'timeAssigned',
        'DISPATCHED': null, // No specific field, just status update
        'CREW_NOTIFIED': null, // No specific field
        'AIRCRAFT_NOTIFIED': 'timeAircraftNotified',
        'AIRCRAFT_LAUNCHED': 'timeAircraftLaunched',
        'AIRCRAFT_AT_CREW_BASE': 'timeAircraftAtCrewBase',
        'CREW_ABOARD': 'timeCrewAboard',
        'ENROUTE_TO_SCENE': 'timeEnrouteToScene',
        'ON_SCENE': 'timeOnScene',
        'PATIENT_CONTACT': 'timePatientContact',
        'TRANSPORTING': 'timeTransporting',
        'AT_DESTINATION': 'timeAtDestination',
        'MISSION_COMPLETE': 'timeMissionComplete',
        'CANCELLED': null,
        'STANDBY': null,
        'CLOSED': null
      };
      
      const statusField = statusToFieldMap[newStatus];
      const updates: any = { status: newStatus };
      
      if (statusField) {
        updates[statusField] = new Date();
      }
      
      await handleUpdateMission(updates);
      
      // Add comment if notes provided
      if (notes) {
        await handleAddComment(notes);
      }
      
      // Don't close modal - let user close it manually
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Status update failed',
        message: error.message
      });
    }
  };

  const handleCloseMission = async (notes: string) => {
    if (!mission) return;
    
    try {
      const updates = {
        status: MissionStatus.CLOSED,
        dateClosed: new Date()
      };
      
      await handleUpdateMission(updates);
      
      // Add closing notes
      if (notes) {
        await handleAddComment(`Mission closed: ${notes}`);
      } else {
        await handleAddComment('Mission closed');
      }
      
      setShowCloseModal(false);
      
      addNotification({
        type: 'success',
        title: 'Mission closed',
        message: 'Mission has been closed successfully'
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to close mission',
        message: error.message
      });
    }
  };

  const handleReopenMission = async (notes: string) => {
    if (!mission) return;
    
    try {
      const updates = {
        status: MissionStatus.RECEIVED,
        dateClosed: null
      };
      
      await handleUpdateMission(updates);
      
      // Add reopening notes
      if (notes) {
        await handleAddComment(`Mission reopened: ${notes}`);
      } else {
        await handleAddComment('Mission reopened');
      }
      
      setShowCloseModal(false);
      
      addNotification({
        type: 'success',
        title: 'Mission reopened',
        message: 'Mission has been reopened successfully'
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to reopen mission',
        message: error.message
      });
    }
  };

  const handleAddComment = async (commentText: string) => {
    if (!mission) return;

    try {
      await missionService.addComment(mission.id, commentText);
      // Comment will be added via socket update
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to add comment',
        message: error.message
      });
    }
  };

  const handleSendAlert = async () => {
    if (!mission) return;

    // Prevent duplicate sends
    if (alertSendingRef.current) {
      debugLog('Alert send already in progress, ignoring duplicate request');
      return;
    }

    alertSendingRef.current = true;
    setSendingAlert(true);

    try {
      await missionService.sendAlert(mission.id);
      setShowAlertModal(false);
      addNotification({
        type: 'success',
        title: 'Alert sent',
        message: 'Mission alert has been sent successfully'
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to send alert',
        message: error.message
      });
    } finally {
      setSendingAlert(false);
      alertSendingRef.current = false;
    }
  };

  const canEdit = user?.role === 'ADMIN' || 
                  user?.role === 'SUPERVISOR' || 
                  user?.role === 'DISPATCHER';
  
  const canReopenMission = user?.role === 'ADMIN' || 
                           user?.role === 'SUPERVISOR';

  const tabs = [
    { id: 'incident', label: 'Incident', icon: '📋' },
    { id: 'lz-safety', label: 'LZ/Safety', icon: '🚁' },
    { id: 'patient', label: 'Patient', icon: '👤' },
    { id: 'resources', label: 'Resources/Scene', icon: '🚒' },
    { id: 'communications', label: 'Communications', icon: '📡' },
    { id: 'audit', label: 'Audit', icon: '🔍' }
  ];

  const missionShortcuts = React.useMemo(() => [
    { key: '1', handler: () => setActiveTab('incident'), description: 'Incident tab' },
    { key: '2', handler: () => setActiveTab('lz-safety'), description: 'LZ/Safety tab' },
    { key: '3', handler: () => setActiveTab('patient'), description: 'Patient tab' },
    { key: '4', handler: () => setActiveTab('resources'), description: 'Resources tab' },
    { key: '5', handler: () => setActiveTab('communications'), description: 'Communications tab' },
    { key: '6', handler: () => setActiveTab('audit'), description: 'Audit tab' },
    { key: 'Escape', handler: () => { setShowStatusModal(false); setShowCloseModal(false); setShowAlertModal(false); }, description: 'Close modal' },
  ], []);

  useKeyboardShortcuts(missionShortcuts, !!mission);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Mission not found</h3>
          <div className="mt-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-primary"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.CRITICAL:
        return 'text-red-600 bg-red-50 border-red-500';
      case Priority.URGENT:
        return 'text-yellow-600 bg-yellow-50 border-yellow-500';
      case Priority.ROUTINE:
        return 'text-green-600 bg-green-50 border-green-500';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Disconnected Banner */}
      <DisconnectedBanner />

      {/* Concurrent Edit Warning */}
      {concurrentEditWarning && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-yellow-800">
            This mission was updated by <strong>{concurrentEditWarning}</strong>. Refresh to see latest changes.
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                const updated = await missionService.getMission(id!);
                setMission(updated);
                setConcurrentEditWarning(null);
              }}
              className="text-sm text-yellow-700 hover:text-yellow-900 underline font-medium"
            >
              Refresh now
            </button>
            <button onClick={() => setConcurrentEditWarning(null)} className="text-yellow-400 hover:text-yellow-600">
              <span className="text-lg">&times;</span>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 no-print">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="mr-2 sm:mr-4 p-2 sm:p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1 sm:gap-2 shadow-md"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                <span className="text-sm font-medium hidden sm:inline">Back</span>
              </button>
              
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {mission.missionNumber}
                  </h1>
                  <span className={clsx(
                    'px-3 py-1 rounded-full text-sm font-medium border',
                    getPriorityColor(mission.priority as Priority)
                  )}>
                    {mission.priority}
                  </span>
                  {mission.isTestTraining && (
                    <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">
                      TEST/TRAINING
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {formatMissionType(mission.missionType)} • {formatMissionStatus(mission.status)}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              {/* Print Button */}
              <button
                onClick={() => window.print()}
                className="btn btn-secondary no-print"
                title="Print mission report"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print
              </button>

              {/* Send Alert Button */}
              {canEdit && mission.status !== MissionStatus.CLOSED && (
                <button
                  onClick={() => setShowAlertModal(true)}
                  disabled={sendingAlert}
                  className="btn bg-orange-500 hover:bg-orange-600 text-white border-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Send Connect Rocket alert"
                >
                  <BellAlertIcon className="h-4 w-4 mr-2" />
                  Send Alert
                </button>
              )}

              {/* Safety Timer Button */}
              <button
                onClick={() => setShowSafetyTimer(true)}
                className="btn bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600"
                title="Start safety check-in timer"
              >
                <ClockIcon className="h-4 w-4 mr-2" />
                Safety Timer
              </button>

              {canEdit && mission.status !== MissionStatus.CLOSED && (
                <button
                  onClick={() => setShowStatusModal(true)}
                  className="btn btn-primary"
                >
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Update Status
                </button>
              )}
              
              {canReopenMission && mission.status === MissionStatus.CLOSED ? (
                <button
                  onClick={() => setShowCloseModal(true)}
                  className="btn btn-secondary text-yellow-600 hover:text-yellow-700"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Reopen Mission
                </button>
              ) : (
                canEdit && (
                  <button
                    onClick={() => setShowCloseModal(true)}
                    className="btn btn-secondary text-red-600 hover:text-red-700"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Close Mission
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Status Stepper */}
      <StatusStepper mission={mission} />

      {/* Mission Summary — always visible */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-3 no-print">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          <div className="flex items-center gap-1.5">
            <MapPinIcon className="h-4 w-4 text-gray-400" />
            <span className="font-medium text-gray-900">{mission.sceneLocation || mission.pickupLocation || 'No location'}</span>
          </div>
          {mission.primaryBase && (
            <div className="flex items-center gap-1.5">
              <BuildingOffice2Icon className="h-4 w-4 text-gray-400" />
              <span className="text-gray-700">{mission.primaryBase.name}</span>
            </div>
          )}
          {mission.callerAgency && (
            <div className="flex items-center gap-1.5">
              <PhoneIcon className="h-4 w-4 text-gray-400" />
              <span className="text-gray-700">{mission.callerAgency}{mission.callerName ? ` — ${mission.callerName}` : ''}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <UserGroupIcon className="h-4 w-4 text-gray-400" />
            <span className="text-gray-700">{mission.patients?.length ?? 0} patient{(mission.patients?.length ?? 0) !== 1 ? 's' : ''}</span>
          </div>
          {mission.timeReceived && (
            <div className="flex items-center gap-1.5 text-gray-500">
              <ClockIcon className="h-4 w-4" />
              <span>{formatTimeAgo(mission.timeReceived)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 no-print">
        <div className="px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2',
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <span className="text-lg">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content - Split Screen Layout */}
      <div className="flex flex-col lg:flex-1 lg:flex-row min-h-0">
        {/* Left Side - Tab Content */}
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6 overflow-y-auto">
          <TabPanel value={activeTab} index="incident">
          <div className="space-y-6">
            <ElapsedTimeTracker mission={mission} />
            <IncidentTab
              mission={mission}
              onUpdate={handleUpdateMission}
              canEdit={canEdit}
            />
          </div>
        </TabPanel>
        
        <TabPanel value={activeTab} index="lz-safety">
          <LZSafetyTab 
            mission={mission} 
            onUpdate={handleUpdateMission}
            canEdit={canEdit}
          />
        </TabPanel>
        
        <TabPanel value={activeTab} index="patient">
          <PatientTab 
            mission={mission} 
            onUpdate={handleUpdateMission}
            onAddPatient={async (patient) => {
              debugLog('MissionDetail: Adding patient', patient);
              const result = await missionService.addPatient(mission.id, patient);
              debugLog('MissionDetail: Patient add result', result);
              
              // Small delay to ensure database transaction completes
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Refresh mission data
              debugLog('MissionDetail: Fetching updated mission');
              const updated = await missionService.getMission(mission.id);
              debugLog('MissionDetail: Updated mission received', {
                patientCount: updated.patientCount,
                patientsLength: updated.patients?.length,
                patients: updated.patients
              });
              setMission(updated);
            }}
            onUpdatePatient={async (patientId, updates) => {
              await missionService.updatePatient(mission.id, patientId, updates);
              const updated = await missionService.getMission(mission.id);
              setMission(updated);
            }}
            onRemovePatient={async (patientId) => {
              try {
                await missionService.removePatient(mission.id, patientId);
                
                // Refresh mission data to get updated patient list
                const updated = await missionService.getMission(mission.id);
                setMission(updated);
                
                addNotification({
                  type: 'success',
                  title: 'Patient deleted',
                  message: 'Patient has been removed from the mission'
                });
              } catch (error: any) {
                addNotification({
                  type: 'error',
                  title: 'Failed to delete patient',
                  message: error.message
                });
              }
            }}
            canEdit={canEdit}
          />
        </TabPanel>
        
        <TabPanel value={activeTab} index="resources">
          <ResourcesTab 
            mission={mission} 
            onUpdate={handleUpdateMission}
            onAddAircraft={async (contractorId, notes) => {
              await missionService.addAircraft(mission.id, { contractorId, notes });
              const updated = await missionService.getMission(mission.id);
              setMission(updated);
            }}
            onRemoveAircraft={async (aircraftId) => {
              await missionService.removeAircraft(mission.id, aircraftId);
              const updated = await missionService.getMission(mission.id);
              setMission(updated);
            }}
            contractors={contractors}
            canEdit={canEdit}
          />
        </TabPanel>
        
        <TabPanel value={activeTab} index="communications">
          <CommunicationsTab 
            mission={mission} 
            onUpdate={handleUpdateMission}
            onAddComment={handleAddComment}
            canEdit={canEdit}
          />
        </TabPanel>
        
        <TabPanel value={activeTab} index="audit">
          <TimelineView missionId={mission.id} mission={mission} />
        </TabPanel>
        </div>

        {/* Comms Panel Toggle */}
        <button
          onClick={() => setShowCommsPanel(!showCommsPanel)}
          className="hidden lg:flex fixed right-0 top-1/2 -translate-y-1/2 z-20 bg-blue-600 text-white px-1.5 py-4 rounded-l-lg shadow-lg hover:bg-blue-700 transition-colors no-print"
          title={showCommsPanel ? 'Hide communications' : 'Show communications'}
        >
          <ChatBubbleLeftRightIcon className="h-5 w-5" />
          {(mission.comments?.length ?? 0) > 0 && (
            <span className="absolute -top-1 -left-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {mission.comments!.length}
            </span>
          )}
        </button>

        {/* Right Side - Communications Panel */}
        {showCommsPanel && (
          <div className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-gray-200 bg-white flex-shrink-0 no-print">
            <CommunicationsPanel
              mission={mission}
              onAddComment={handleAddComment}
              height="100%"
            />
          </div>
        )}
      </div>

      {/* Print Mission Report */}
      <PrintMissionReport mission={mission} />

      {/* Status Update Modal */}
      <StatusUpdateModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={mission.status as MissionStatus}
        onStatusUpdate={handleStatusChange}
        onAircraftEvent={(_event, notes) => {
          // Add aircraft event as a comment
          if (notes) {
            // Don't await - let it run in background to avoid blocking UI
            handleAddComment(notes).catch(error => {
              console.error('Failed to log aircraft event:', error);
            });
          }
        }}
        missionComments={mission.comments || []}
      />

      {/* Close/Reopen Mission Modal */}
      <CloseMissionModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={mission.status === MissionStatus.CLOSED ? handleReopenMission : handleCloseMission}
        missionNumber={mission.missionNumber}
        isReopening={mission.status === MissionStatus.CLOSED}
      />

      {/* Safety Timer Modal */}
      <SafetyTimerModal
        isOpen={showSafetyTimer}
        onClose={() => setShowSafetyTimer(false)}
        missionNumber={mission.missionNumber}
      />

      {/* Send Alert Modal */}
      {showAlertModal && (
        <SendAlertModal
          mission={mission}
          onConfirm={handleSendAlert}
          onCancel={() => setShowAlertModal(false)}
          sending={sendingAlert}
        />
      )}
    </div>
  );
};

export default MissionDetail;